/**
 * אורקסטרציה מרוכזת לפתיחת תשלום מצ'אט לקוח — lock, dedupe, openKey יציב, לוגים.
 */
import {
  clearChatPaymentLock,
  setChatPaymentLockedOffer,
} from "../stores/customerChatPaymentStore";

/** @type {string|null} */
let lastCompletedOpenKey = null;

export function resetChatPaymentOpenDedupe() {
  lastCompletedOpenKey = null;
}

function stableSortedJson(obj) {
  if (obj === null || typeof obj !== "object") return JSON.stringify(obj);
  if (Array.isArray(obj)) return `[${obj.map(stableSortedJson).join(",")}]`;
  const keys = Object.keys(obj).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stableSortedJson(obj[k])}`).join(",")}}`;
}

function passengersHash(roomsAndPaxInfo) {
  if (!Array.isArray(roomsAndPaxInfo)) return "pax:empty";
  const slim = roomsAndPaxInfo.map((r) => ({
    roomCode: r?.room?.roomCode ?? r?.room?.code ?? r?.room?.roomId ?? null,
    adults: r?.adults ?? null,
    children: r?.children ?? null,
    childAges: r?.childAges ?? null,
  }));
  return stableSortedJson(slim);
}

function tripHash(selectedResult) {
  if (!selectedResult || typeof selectedResult !== "object") return "trip:none";
  const dep =
    selectedResult.departureDate ??
    selectedResult.departure_date ??
    selectedResult.depDate ??
    null;
  const ret =
    selectedResult.returnDate ?? selectedResult.return_date ?? selectedResult.retDate ?? null;
  const dest =
    selectedResult.destination ??
    selectedResult.destinationCode ??
    selectedResult.hotelAllocation?.destination ??
    null;
  return stableSortedJson({ dep, ret, dest, dealId: selectedResult.dealId ?? null });
}

/**
 * @param {{ offerId: string|null, approvedPriceIls: number, passengersHash: string, tripHash: string }} p
 */
export function buildChatPaymentStableOpenKey(p) {
  return stableSortedJson({
    offerId: p.offerId,
    approvedPriceIls: p.approvedPriceIls,
    passengersHash: p.passengersHash,
    tripHash: p.tripHash,
  });
}

/**
 * @param {{
 *   reply: Record<string, unknown>,
 *   sessionId: string|null,
 *   agentId: string|null,
 *   selectedResult: unknown,
 *   roomsAndPaxInfo: unknown,
 *   modalOpenRef: { current: boolean },
 *   traceId?: string|null,
 *   onOpenModal: () => void,
 * }} args
 * @returns {{ ok: boolean, error_code?: string }}
 */
export function processChatPaymentClientActions(args) {
  const {
    reply,
    sessionId,
    agentId,
    selectedResult,
    roomsAndPaxInfo,
    modalOpenRef,
    traceId,
    onOpenModal,
  } = args;

  const actions = reply?.client_actions;
  if (!Array.isArray(actions)) return { ok: true };

  const payAction = actions.find((a) => a?.type === "open_vacation_package_payment");
  if (!payAction) return { ok: true };

  const lockAgentId =
    payAction?.agent_id != null && String(payAction.agent_id).trim() !== ""
      ? String(payAction.agent_id).trim()
      : agentId != null && String(agentId).trim() !== ""
        ? String(agentId).trim()
        : null;

  // eslint-disable-next-line no-console
  console.info("CHAT_PAYMENT_FLOW_START", {
    traceId: traceId ?? null,
    sessionId,
    hasPaymentBlock: Boolean(reply?.payment),
  });

  const payment = reply?.payment && typeof reply.payment === "object" ? reply.payment : null;
  const sr = selectedResult && typeof selectedResult === "object" ? selectedResult : null;

  const fromReply =
    payment != null && payment.price_ils != null ? Number(payment.price_ils) : NaN;
  const fromCard =
    sr?.price_ils != null
      ? Number(sr.price_ils)
      : sr?.vacation_results_display_price_ils != null
        ? Number(sr.vacation_results_display_price_ils)
        : NaN;

  /** Server payment block wins over card state — same pipeline as ranked offers / SSOT. */
  let approvedPriceIls;
  if (Number.isFinite(fromReply)) {
    approvedPriceIls = Math.round(fromReply);
    if (Number.isFinite(fromCard) && Math.round(fromCard) !== approvedPriceIls) {
      // eslint-disable-next-line no-console
      console.warn("CHAT_PAYMENT_PRICE_RECONCILE", {
        reason: "reply_overrides_selectedResult",
        usedIls: approvedPriceIls,
        selectedResultIls: Math.round(fromCard),
      });
    }
  } else if (Number.isFinite(fromCard)) {
    approvedPriceIls = Math.round(fromCard);
  } else {
    // eslint-disable-next-line no-console
    console.warn("CHAT_PAYMENT_FLOW_START", { error: "missing_price_ils" });
    return { ok: false, error_code: "PRICE_MISSING" };
  }

  const paymentPriceIls = approvedPriceIls;

  const offerIdRaw =
    (payment && payment.offer_id != null ? String(payment.offer_id) : null) ??
    (sr?.offer_id != null ? String(sr.offer_id) : null) ??
    (sr?.dealId != null ? String(sr.dealId) : null);
  const offerId = offerIdRaw && offerIdRaw.trim() !== "" ? offerIdRaw.trim() : null;

  const ph = passengersHash(roomsAndPaxInfo);
  const th = tripHash(sr);
  const openKey = buildChatPaymentStableOpenKey({
    offerId,
    approvedPriceIls,
    passengersHash: ph,
    tripHash: th,
  });

  if (modalOpenRef?.current) {
    if (lastCompletedOpenKey === openKey) {
      // eslint-disable-next-line no-console
      console.info("CHAT_PAYMENT_MODAL_SKIPPED_DUPLICATE", {
        reason: "same_open_key",
        openKeyPrefix: openKey.slice(0, 80),
      });
    } else {
      // eslint-disable-next-line no-console
      console.info("CHAT_PAYMENT_MODAL_SKIPPED_DUPLICATE", { reason: "modal_already_open_other_flow" });
    }
    return { ok: true };
  }

  const lock = {
    offerId,
    chatSessionId: sessionId != null ? String(sessionId) : null,
    agentId: lockAgentId,
    approvedPriceIls,
    paymentPriceIls,
    currency: "ILS",
    passengersHash: ph,
    tripHash: th,
    createdAt: Date.now(),
    source: "customer_chat_client_action",
  };

  setChatPaymentLockedOffer(lock);
  lastCompletedOpenKey = openKey;

  // eslint-disable-next-line no-console
  console.info("CHAT_PAYMENT_LOCKED_OFFER_SET", {
    offerId: lock.offerId,
    approvedPriceIls: lock.approvedPriceIls,
    openKeyLen: openKey.length,
  });

  // eslint-disable-next-line no-console
  console.info("CHAT_PAYMENT_PRICE_RENDERED", {
    source: "locked_offer",
    paymentPriceIls: lock.paymentPriceIls,
  });

  if (modalOpenRef) modalOpenRef.current = true;
  onOpenModal();
  // eslint-disable-next-line no-console
  console.info("CHAT_PAYMENT_MODAL_OPENED", { via: "orchestrator" });
  return { ok: true };
}

/**
 * פתיחה ידנית (כפתור fallback) — אותה לוגיקת lock בלי client_action.
 */
export function processChatPaymentManualOpen(args) {
  const { agentId, paymentFromMessage, ...rest } = args;
  const syntheticReply = {
    client_actions: [
      { type: "open_vacation_package_payment", agent_id: agentId ?? null },
    ],
    payment: paymentFromMessage,
  };
  return processChatPaymentClientActions({
    ...rest,
    reply: syntheticReply,
    agentId,
  });
}

export function onChatPaymentModalClosed() {
  clearChatPaymentLock();
  resetChatPaymentOpenDedupe();
}
